















































































import {
  defineComponent,
  ref,
  computed,
  watch,
  onBeforeUnmount,
  Ref,
} from '@vue/composition-api';
import {
  Popup, Loading, Button, Toast,
} from '@hb/coffee';
import { UpArrowOutline12, DownArrowOutline12 } from '@hb/coffee-icons';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { ConvergeAgreementApi } from '@/api/convergeAgreement';
import type { AgreementListData } from '@/api/convergeAgreement/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { isNeedAlwaysAgreeProtocol } from '@hb/liveness';
import { LivenessProjectEnum, LivenessSceneEnum } from '@/enums/liveness-param';
import { livenessPop } from '@/components/liveness-pop';
import type { LivenessLaunchResponseType } from '@hb/liveness/src/api/liveness/types';
import { openThirdPage } from '@/api/native/utils';
import { formatAgreementList, buildSubmitAgreementParams } from '@/shared/agreement';
import type { ConvergeAgreementItem } from '@/shared/agreement';
import { useCountDown } from '@/hooks/useCountDown';
import { jumpToContractList } from '@/shared/utils/jumpTo';
import { nameMaskFormat, idNoMaskFormart } from '@/shared/utils/dataMaskFormat';
import store from '@/store';
import { LivenessSignAgreementsPopEvents } from './event-names';
import type { LivenessSignAgreementsPopProps } from './types';
import { TP_LIVENESS_SIGN_AGREEMENTS_POP_MODULE } from './tianqi';

/**
* 异步赋值辅助函数
* 用于简化异步数据赋值到 Vue ref 的操作
*/
export const assignAsync = <T>(targetRef: Ref<T>, resultPromise: Promise<T>)
  : Promise<void> => resultPromise.then((res) => { targetRef.value = res; });

export default defineComponent({
  name: 'LivenessSignAgreementsPop',
  components: {
    Popup,
    Loading,
    Button,
    UpArrowOutline12,
    DownArrowOutline12,
  },
  props: {
    /** 弹窗显示控制 */
    show: {
      type: Boolean,
      required: true,
    },
    /** 是否命中ocr后置提现流程 */
    ocrAfterLoan: {
      type: Boolean,
      required: true,
    },
    /** 是否已有二要素信息 */
    hasNameAndIdCard: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    LivenessSignAgreementsPopEvents.UPDATE_SHOW as string,
    LivenessSignAgreementsPopEvents.AGREEMENT_CONFIRMED as string,
    LivenessSignAgreementsPopEvents.LIVENESS_SUCCESS as string,
    LivenessSignAgreementsPopEvents.LIVENESS_FAIL as string,
    LivenessSignAgreementsPopEvents.CLOSE as string,
    LivenessSignAgreementsPopEvents.CANCEL as string,
  ],
  setup(
    props: LivenessSignAgreementsPopProps,
    { emit }: { emit: (event: string, ...args: any[]) => void },
  ) {
    /** Loading状态 */
    const isLoading = ref(false);
    /** 协议列表 */
    const contractList = ref<ConvergeAgreementItem[]>([]);
    /** 原始协议查询数据 */
    const agreementListData = ref<AgreementListData | null>(null);
    /** 用户实名信息 */
    const userInfo = ref<{ name: string; idCardNo: string }>({
      name: '',
      idCardNo: '',
    });
    const ubtCommonParams = computed(() => ({
      agreements: contractList.value.map((item) => item.name).join(','),
      hasNameAndIdCard: props.hasNameAndIdCard ? 1 : 0,
    }));
    /** 是否需要始终同意协议 */
    const alwaysLivenessAgree = ref(false);
    /** 活体认证启动事务ID */
    const livenessLaunchTransaction = ref<LivenessLaunchResponseType>();
    /** 是否展开显示全部协议 */
    const showAllAgreements = ref(false);
    /** 初始化倒计时 */
    const {
      countDown,
      showCountDown,
      startCountDown,
      clearCountDown,
    } = useCountDown(3, false);
    /** 查询协议定时器 */
    let queryTimer: NodeJS.Timeout | null = null;

    /** 获取 extraData 透传参数 */
    const getExtraData = (): Record<string, unknown> | undefined => (
      store.state.base.businessInfoExtra?.userInfo?.extraData
    );

    /** 初始化查询协议列表参数 */
    const initQueryAgreementParams = async (): Promise<void> => {
      try {
        await Promise.all([
          assignAsync(alwaysLivenessAgree, isNeedAlwaysAgreeProtocol()),
          assignAsync(livenessLaunchTransaction, livenessPop.prefetchTransitionContext({
            project: LivenessProjectEnum.ZYQ,
            scene: LivenessSceneEnum.Authority,
            ocrAfterLoan: true,
          })),
        ]);
      } catch (error) {
        ubtSendError({
          name: `initQueryAgreementParams-error-${(error as { code: string }).code}`,
          message: (error as Error).message,
        });
      }
    };

    /** 查询协议列表（轮询直到 loading 为 false） */
    const queryAgreementList = async (): Promise<void> => {
      try {
        const data = await ConvergeAgreementApi.queryAgreement({
          transactionId: livenessLaunchTransaction.value?.transactionId || '',
          needAuth: alwaysLivenessAgree.value || false,
          extraData: getExtraData(),
        });

        // 保存原始数据，用于提交协议
        agreementListData.value = data;
        contractList.value = formatAgreementList(data);
        userInfo.value = {
          name: data.userName,
          idCardNo: data.idCardNo,
        };

        /** 若展示"征信协议"，则强制用户读取协议3秒 */
        if (data.creditRouteChannelAgreement?.list?.length) {
          startCountDown();
        }

        /** 如果还在加载中，则继续每秒的轮询 */
        if (data.loading) {
          isLoading.value = true;
          queryTimer = setTimeout(() => {
            queryAgreementList();
          }, 1000);
        } else {
          isLoading.value = false;
        }
      } catch (error) {
        ubtSendError({
          name: `queryAgreementList-error-${(error as { code: string }).code}`,
          message: (error as Error).message,
        });
      }
    };

    /** 清除查询定时器 */
    const clearQueryTimer = () => {
      if (queryTimer) {
        clearTimeout(queryTimer);
        queryTimer = null;
      }
    };

    /** 处理弹窗显示状态更新 */
    const onUpdateShow = (value: boolean) => {
      emit(LivenessSignAgreementsPopEvents.UPDATE_SHOW, value);
      if (!value) {
        emit(LivenessSignAgreementsPopEvents.CLOSE);
        isLoading.value = false;
        clearCountDown();
        clearQueryTimer();
      }
    };
    /** 处理协议确认 */
    const onAgreementConfirm = async () => {
      try {
        ubtSetupPageButton(TP_LIVENESS_SIGN_AGREEMENTS_POP_MODULE, {
          ...ubtCommonParams.value,
          buttonName: '同意',
        });

        if (!agreementListData.value || !livenessLaunchTransaction.value?.transactionId) {
          ubtSendError({
            name: 'onAgreementConfirm-error',
            message: 'agreementListData-or-livenessLaunchTransaction-not-found',
          });
          Toast('网络异常，请重试');
          onUpdateShow(false);
          return;
        }
        const params = buildSubmitAgreementParams(
          agreementListData.value,
          livenessLaunchTransaction.value.transactionId,
        );
        // 提交协议
        await ConvergeAgreementApi.submitAgreement({
          ...params,
          extraData: getExtraData(),
        });
        emit(LivenessSignAgreementsPopEvents.AGREEMENT_CONFIRMED, {
          launchTransaction: livenessLaunchTransaction.value,
          agreementListData: agreementListData.value,
        });
      } catch (error) {
        ubtSendError({
          name: `onAgreementConfirm-error-${(error as { code: string }).code}`,
          message: (error as Error).message,
        });
      }
    };


    /** 处理关闭按钮点击 */
    const onClose = () => {
      ubtSetupPageButton(TP_LIVENESS_SIGN_AGREEMENTS_POP_MODULE, {
        ...ubtCommonParams.value,
        buttonName: '关闭',
      });
      emit(LivenessSignAgreementsPopEvents.CLOSE);
      onUpdateShow(false);
    };

    /** 处理协议点击 */
    const onAgreementClick = (convergeItem: ConvergeAgreementItem) => {
      /** 如果协议超过1条，则跳转列表页查看协议 */
      if (convergeItem.list.length > 1) {
        jumpToContractList(convergeItem.list);
      } else {
        openThirdPage(convergeItem.list[0].agreementUrl);
      }
    };

    /** 监听 show 变化，重置步骤 */
    watch(
      () => props.show,
      async (show) => {
        if (show) {
          /** 查询活体协议所需参数 */
          await initQueryAgreementParams();
          /** 查询协议列表 */
          await queryAgreementList();

          ubtSetupModuleExpose(TP_LIVENESS_SIGN_AGREEMENTS_POP_MODULE, {
            ...ubtCommonParams.value,
          });
        } else {
          // 弹窗关闭时，清除倒计时和查询定时器
          clearCountDown();
          clearQueryTimer();
        }
      },
    );

    /** 组件卸载时清除定时器 */
    onBeforeUnmount(() => {
      clearQueryTimer();
    });

    /** 脱敏后的姓名 */
    const maskedUserName = computed(() => (userInfo.value.name ? nameMaskFormat(userInfo.value.name) : ''));

    /** 脱敏后的身份证号 */
    const maskedIdCardNo = computed(() => (userInfo.value.idCardNo ? idNoMaskFormart(userInfo.value.idCardNo) : ''));

    /** 显示的协议列表：展开时显示全部，收起时只显示前2个 */
    const displayContractList = computed(() => {
      if (showAllAgreements.value) {
        return contractList.value;
      }
      return contractList.value.slice(0, 2);
    });

    return {
      contractList,
      isLoading,
      countDown,
      userInfo,
      maskedUserName,
      maskedIdCardNo,
      showCountDown,
      showAllAgreements,
      displayContractList,

      onUpdateShow,
      onClose,
      onAgreementClick,
      onAgreementConfirm,
    };
  },
});
